import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, FileText, Phone, Send, ArrowRight, Eye, Shield, Clock, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSettingsStore } from '../lib/stores/settingsStore';
import { useDealStore } from '../lib/stores/dealStore';
import { useOtpStore } from '../lib/stores/otpStore';
import { generateQuotePublicPDF } from '../lib/pdf';
import { useApp } from '../components/AppProvider';

interface SignatoryInfo {
  id: string;
  name: string;
  email: string;
  role: string;
  required: boolean;
  status: string;
}

export function EnhancedSignaturePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [deal, setDeal] = useState<any>(null);
  const [dealServices, setDealServices] = useState<any[]>([]);
  const [signatureRequest, setSignatureRequest] = useState<any>(null);
  const [currentSignatory, setCurrentSignatory] = useState<SignatoryInfo | null>(null);
  const [allSignatories, setAllSignatories] = useState<SignatoryInfo[]>([]);
  const { settings, fetchSettings } = useSettingsStore();
  // Dati dell'azienda che ha emesso il preventivo, dalla RPC: il cliente non ha login,
  // quindi useSettingsStore resta vuoto e prima bloccava la firma.
  const [company, setCompany] = useState<Record<string, any> | null>(null);
  const { handleDealSigned } = useDealStore();
  const { sendOtp, verifyOtp } = useOtpStore();
  const { toast } = useApp();
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);

  // OTP states
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  
  // Contract preview states
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);

  // Recapiti dove inviare PDF firmato: precompilati da signature_request o client,
  // modificabili dal firmatario prima di confermare.
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyPhonePrefix, setNotifyPhonePrefix] = useState('+39');
  const [notifyPhoneNumber, setNotifyPhoneNumber] = useState('');

  // Step tracking
  const [currentStep, setCurrentStep] = useState(1); // 1: Review, 2: Verify, 3: Sign
  
  useEffect(() => {
    const fetchSignatureRequest = async () => {
      setLoading(true);
      try {
        if (!id) {
          setError('Invalid signature link');
          return;
        }

        // Accesso via RPC SECURITY DEFINER: pagina anon, tabelle base blindate (RLS).
        // La RPC ritorna request + deal + client + signatories lavorando solo sul token.
        const { data: rpcData, error: requestError } = await supabase
          .rpc('get_signature_request_by_token', { p_token: id });
        const requestData = rpcData?.request ?? null;
        setCompany(rpcData?.company ?? null);

        if (requestError) {
          console.error('Error loading signature request:', requestError);
          setError('Signature request not found or has expired');
          return;
        }
        
        if (!requestData) {
          setError('Signature request not found');
          return;
        }
        
        // Check request status
        if (requestData.status !== 'pending') {
          if (requestData.status === 'completed') {
            setSuccess(true);
            setError('This document has already been signed');
          } else {
            setError(`This signature request is ${requestData.status}`);
          }
          return;
        }
        
        // Check expiry date
        if (requestData.expires_at) {
          const expiryDate = new Date(requestData.expires_at);
          if (expiryDate < new Date()) {
            setError('This signature request has expired');
            return;
          }
        }
        
        setSignatureRequest(requestData);
        setDeal(requestData.deal);
        setAllSignatories(requestData.signatories || []);

        // Precompila recapiti notifica: prima da signature_request.notification_email/phone
        // (se l'operatore li ha valorizzati dal SendProposalModal), fallback su client.
        const clientObj = requestData.deal?.client;
        const initialEmail = requestData.notification_email
          || clientObj?.email
          || '';
        const initialPhoneRaw = (requestData.notification_phone
          || clientObj?.whatsapp_number
          || clientObj?.phone
          || '').toString().trim();
        setNotifyEmail(initialEmail);
        // Parse prefix vs numero: se inizia con +, separa fino al primo spazio o (3-4 caratteri)
        if (initialPhoneRaw.startsWith('+')) {
          const m = initialPhoneRaw.match(/^(\+\d{1,4})\s*(.*)$/);
          if (m) {
            setNotifyPhonePrefix(m[1]);
            setNotifyPhoneNumber(m[2].replace(/\s+/g, ''));
          } else {
            setNotifyPhoneNumber(initialPhoneRaw);
          }
        } else {
          setNotifyPhoneNumber(initialPhoneRaw.replace(/\s+/g, ''));
        }
        
        // Find current signatory based on email or default to first
        const firstPendingSignatory = requestData.signatories?.find(
          (s: SignatoryInfo) => s.status === 'pending'
        );
        
        if (firstPendingSignatory) {
          setCurrentSignatory(firstPendingSignatory);
          setPhone(firstPendingSignatory.email || '');
        }

        // Check if OTP verification is required
        const requestSettings = requestData.settings || {};
        if (requestSettings.require_otp) {
          setShowOtpStep(true);
        }

        // Deal services dalla stessa RPC (già joinati con service + payment_method)
        setDealServices(rpcData?.deal_services || []);

        // Fetch company settings
        await fetchSettings();
        
        // Log view action. La RPC prende il token, non l'id della richiesta: la
        // pagina e' pubblica e il client non deve poter scegliere su quale
        // richiesta scrivere.
        const { error: auditError } = await supabase.rpc('log_signature_action_by_token', {
          p_token: id,
          p_action: 'viewed',
          p_user_agent: navigator.userAgent
        });
        if (auditError) {
          console.error('Audit firma non registrato (viewed):', auditError);
        }

      } catch (err) {
        console.error('Error loading signature request:', err);
        setError('Failed to load signature request');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSignatureRequest();
  }, [id, fetchSettings]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    
    // Set up canvas for signature
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [showSignatureCanvas]);

  const handleSendOtp = async () => {
    if (!phone) {
      toast.error('Please enter a phone number');
      return;
    }

    try {
      const success = await sendOtp(phone);
      if (success) {
        setOtpSent(true);
        toast.success('Verification code sent successfully');
      } else {
        toast.error('Failed to send verification code');
      }
    } catch (error) {
      toast.error('Failed to send verification code');
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode) {
      toast.error('Please enter the verification code');
      return;
    }

    try {
      const success = await verifyOtp(phone, otpCode);
      if (success) {
        setOtpVerified(true);
        setCurrentStep(2);
        toast.success('Phone number verified successfully');
      } else {
        toast.error('Invalid verification code');
      }
    } catch (error) {
      toast.error('Failed to verify code');
    }
  };
  
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    setIsDrawing(true);
    
    // Get position
    let x, y;
    if ('touches' in e) {
      const rect = canvas.getBoundingClientRect();
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.nativeEvent.offsetX;
      y = e.nativeEvent.offsetY;
    }
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Get position
    let x, y;
    if ('touches' in e) {
      const rect = canvas.getBoundingClientRect();
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.nativeEvent.offsetX;
      y = e.nativeEvent.offsetY;
    }
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  
  const endDrawing = () => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.closePath();
    setIsDrawing(false);
    
    // Save signature data
    setSignatureData(canvas.toDataURL());
  };
  
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  // Funzione per convertire base64 PNG in JPEG
  const convertToTransparentPng = (base64Data: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        if (ctx) {
          // PNG con sfondo trasparente — niente fillRect bianco
          ctx.drawImage(img, 0, 0);
          const pngBase64 = canvas.toDataURL('image/png');
          resolve(pngBase64);
        } else {
          reject(new Error('Canvas context not available'));
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = base64Data;
    });
  };

  const submitSignature = async () => {
    if (!signatureData || !signatureRequest || !deal || !currentSignatory) return;

    setLoading(true);
    try {
      // Converti la firma in PNG trasparente (no sfondo bianco) — supportato dal
      // <img> nel PDF (processTemplate riga 934) e dai client email che ricevono
      // l'allegato. Storage path resta 'signatures/' nel bucket 'uploads'.
      const pngSignatureData = await convertToTransparentPng(signatureData);

      const signatureFileName = `signatures/${deal.id}-${currentSignatory.id}-${Date.now()}.png`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('uploads')
        // upsert:false — il cliente non ha login e su storage ha solo la policy INSERT:
        // con upsert la RLS rifiuta l'upload (stesso difetto corretto in SignaturePage
        // il 02/09/2026). Il nome ha gia' il timestamp, niente collisioni.
        .upload(signatureFileName, base64ToBlob(pngSignatureData), {
          contentType: 'image/png',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get the public URL for the signature
      const { data: { publicUrl: signatureUrl } } = supabase.storage
        .from('uploads')
        .getPublicUrl(signatureFileName);

      // Firma il firmatario + verifica "tutti i richiesti firmati" via RPC SECURITY DEFINER.
      // Le tabelle base sono blindate: la RPC valida il token e che il firmatario appartenga
      // alla richiesta (anti-tampering), poi calcola se tutti i richiesti hanno firmato.
      const { data: signResult, error: signatoryError } = await supabase.rpc('sign_signatory_by_token', {
        p_token: id,
        p_signatory_id: currentSignatory.id,
        p_signature_url: signatureUrl,
        p_otp_verified: otpVerified,
      });

      if (signatoryError) throw signatoryError;

      // Log signature action (best-effort, ma l'errore va almeno visto: e' la
      // traccia di una firma con valore legale)
      const { error: signAuditError } = await supabase.rpc('log_signature_action_by_token', {
        p_token: id,
        p_action: 'signed',
        p_signatory_id: currentSignatory.id,
        p_user_agent: navigator.userAgent
      });
      if (signAuditError) {
        console.error('Audit firma non registrato (signed):', signAuditError);
      }

      const allRequiredSigned = signResult?.all_required_signed ?? false;

      if (allRequiredSigned) {
        // Tutti i firmatari richiesti hanno firmato: genera PDF finale via
        // flusia-core (auth: signature_token). La firma del firmatario corrente
        // è già stata uploadata su `uploads`, qui usiamo il data URI del canvas
        // direttamente per minimizzare round-trip.
        if (!id) throw new Error('Missing signature token');
        const pdfBlob = await generateQuotePublicPDF({
          signatureToken: id,
          signed: true,
          signatureDataUrl: pngSignatureData,
          signerName: currentSignatory?.name ?? undefined,
          signerEmail: currentSignatory?.email ?? undefined,
        });
        // Cartella con timestamp e niente upsert, come in SignaturePage: l'anonimo
        // non puo' sovrascrivere e lo stesso preventivo puo' essere rifirmato.
        const pdfFileName = `${deal.id}/${Date.now()}/${deal.id}-fully-signed.pdf`;

        const { error: pdfError } = await supabase.storage
          .from('signed-pdfs')
          .upload(pdfFileName, pdfBlob, {
            contentType: 'application/pdf',
            upsert: false
          });

        if (pdfError) throw pdfError;

        const { data: { publicUrl: pdfUrl } } = supabase.storage
          .from('signed-pdfs')
          .getPublicUrl(pdfFileName);

        // Finalize via RPC SECURITY DEFINER. Il PATCH diretto su deals da role anon
        // falliva con "permission denied for table user_profiles" a causa della
        // catena di trigger AFTER UPDATE su deals (audit_logs → auto_generated_notes
        // → ...). La RPC esegue come postgres, bypassa il problema, e fa atomicamente
        // UPDATE deals + signature_requests con verifica del token come auth.
        // Popola anche signed_contract_url così il PDF firmato compare nella UI deal
        // come se fosse caricato dal bottone "Carica firmato" (DealDetailsPage:415).
        if (!id) throw new Error('Missing signature token for finalize');

        // Salva eventuali modifiche ai recapiti notifica fatte dal firmatario.
        // La policy signature_requests_public_access ALL permette UPDATE da anon.
        const composedPhone = notifyPhoneNumber.trim()
          ? `${notifyPhonePrefix}${notifyPhoneNumber.replace(/\s+/g, '')}`
          : '';
        await supabase.rpc('update_signature_notification_by_token', {
          p_token: id,
          p_email: notifyEmail.trim() || null,
          p_phone: composedPhone || null,
        });

        const { error: finalizeError } = await supabase.rpc('finalize_signed_deal', {
          p_signature_token: id,
          p_deal_id: deal.id,
          p_signature_url: signatureUrl,
          p_signed_pdf_url: pdfUrl,
          p_signed_at: new Date().toISOString(),
        });
        if (finalizeError) throw finalizeError;

        // Post-firma: triggera invio PDF firmato al cliente (email/WA) se la
        // signature_request ha notification_email/notification_phone. Non
        // blocchiamo l'UX se fallisce — l'utente vede comunque success.
        try {
          const CORE_URL = import.meta.env.VITE_CORE_URL || 'https://core.flusia.it';
          const notifyHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
          const devKey = import.meta.env.VITE_FLUSIA_CORE_DEV_KEY;
          if (devKey) notifyHeaders['X-Flusia-Dev-Key'] = devKey;
          const notifyResp = await fetch(`${CORE_URL}/api/documents/quote/public/notify`, {
            method: 'POST',
            headers: notifyHeaders,
            body: JSON.stringify({ signatureToken: id }),
          });
          if (!notifyResp.ok) {
            console.warn('Notify post-signature non-fatal failure:', notifyResp.status, await notifyResp.text().catch(() => ''));
          }
        } catch (notifyErr) {
          console.warn('Notify post-signature error (non-fatal):', notifyErr);
        }

        // Create project if applicable
        try {
          if (typeof handleDealSigned === 'function') {
            await handleDealSigned(deal.id);
          }
        } catch (dealSignedError) {
          console.error('Error creating project from deal:', dealSignedError);
        }
      }
      
      setSuccess(true);
      
    } catch (error) {
      console.error('Error submitting signature:', error);
      setError('Failed to submit signature. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const base64ToBlob = (base64: string) => {
    const parts = base64.split(';base64,');
    const contentType = parts[0].split(':')[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: contentType });
  };

  const proceedToSignature = () => {
    setCurrentStep(3);
    setShowSignatureCanvas(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-600">Loading signature request...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Error</h3>
            <p className="mt-2 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#0f6cbd] hover:bg-[#0a5ba8]"
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Document Signed Successfully</h3>
            <p className="mt-2 text-sm text-gray-500">
              Thank you for signing the document. The signature has been recorded and all parties will be notified.
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.close()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#0f6cbd] hover:bg-[#0a5ba8]"
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // OTP verification step
  if (showOtpStep && !otpVerified) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="mt-4 text-xl font-bold text-gray-900">Security Verification</h2>
            <p className="mt-2 text-sm text-gray-500">
              Please verify your identity to continue with the signature process
            </p>
          </div>

          <div className="space-y-6">
            {!otpSent ? (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1234567890"
                  />
                </div>
                <button
                  onClick={handleSendOtp}
                  className="mt-4 w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0f6cbd] hover:bg-[#0a5ba8]"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Verification Code
                </button>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Enter Verification Code
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                  />
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <button
                    onClick={() => setOtpSent(false)}
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    Change Phone Number
                  </button>
                  <button
                    onClick={handleSendOtp}
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    Resend Code
                  </button>
                </div>
                <button
                  onClick={handleVerifyOtp}
                  className="mt-4 w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0f6cbd] hover:bg-[#0a5ba8]"
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Verify and Continue
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-[#0f6cbd] text-white' : 'bg-gray-200'}`}>
                  <Eye className="h-4 w-4" />
                </div>
                <span className="ml-2 text-sm font-medium">Review</span>
              </div>
              <div className={`w-16 h-0.5 ${currentStep >= 2 ? 'bg-[#0f6cbd]' : 'bg-gray-200'}`} />
              <div className={`flex items-center ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-[#0f6cbd] text-white' : 'bg-gray-200'}`}>
                  <Shield className="h-4 w-4" />
                </div>
                <span className="ml-2 text-sm font-medium">Verify</span>
              </div>
              <div className={`w-16 h-0.5 ${currentStep >= 3 ? 'bg-[#0f6cbd]' : 'bg-gray-200'}`} />
              <div className={`flex items-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-[#0f6cbd] text-white' : 'bg-gray-200'}`}>
                  <FileText className="h-4 w-4" />
                </div>
                <span className="ml-2 text-sm font-medium">Sign</span>
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-8 p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Digital Signature Request</h1>
              <p className="mt-1 text-sm text-gray-500">
                {deal?.client?.business_name} - {deal?.title}
              </p>
              {currentSignatory && (
                <p className="mt-1 text-sm text-blue-600">
                  Signing as: {currentSignatory.name} ({currentSignatory.role})
                </p>
              )}
            </div>
            
            {!showSignatureCanvas && currentStep < 3 && (
              <button
                onClick={proceedToSignature}
                className="mt-4 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#0f6cbd] hover:bg-[#0a5ba8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FileText className="h-4 w-4 mr-2" />
                Proceed to Signature
              </button>
            )}
          </div>

          {/* Custom message */}
          {signatureRequest?.custom_message && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">{signatureRequest.custom_message}</p>
            </div>
          )}
        </div>
        
        {/* Contract Preview - Same as original but with improved layout */}
        <div className="mb-6 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden p-6">
            <div className="mb-8 text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{deal?.title}</h1>
              <p className="text-gray-600">{deal?.client?.business_name}</p>
              {deal?.quote_number && <p className="text-gray-500 text-sm mt-1">Quote #{deal.quote_number}</p>}
            </div>
            
            {/* Deal Details */}
            {deal?.description && (
              <div className="mb-8">
                <h2 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Description</h2>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: deal.description }}
                />
              </div>
            )}
            
            {/* Services */}
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Services</h2>
              <div className="space-y-4">
                {dealServices.map((service) => (
                  <div key={service.id} className="flex justify-between items-start p-3 bg-gray-50 rounded-md">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {service.service?.title}
                        {service.service?.service_type === 'monthly' && (
                          <span className="text-xs text-gray-500 ml-1">/month</span>
                        )}
                      </h3>
                      {service.service?.short_description && (
                        <p className="mt-1 text-sm text-gray-500">
                          {service.service.short_description}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {service.discounted_amount ? (
                        <div>
                          <span className="line-through text-gray-400">
                            €{service.amount.toFixed(2)}
                          </span>
                          <span className="ml-2 text-green-600 font-medium">
                            €{service.discounted_amount.toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <span className="font-medium">€{service.amount.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex justify-end text-lg font-medium pt-4 border-t">
                  Total: €{dealServices.reduce((sum, service) => 
                    sum + (service.discounted_amount || service.amount), 0
                  ).toFixed(2)}
                </div>
              </div>
            </div>
            
            {/* Terms and Conditions */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Terms and Conditions</h2>
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: (company ?? settings)?.terms_and_conditions || '' }} />
              </div>
            </div>
          </div>
        </div>
        
        {/* Signature Section */}
        {showSignatureCanvas && (
          <div id="signature-section" className="bg-white shadow rounded-lg mb-8 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Your Digital Signature</h2>
            <p className="text-sm text-gray-500 mb-4">
              Please sign below to accept the terms of this contract
            </p>

            {/* Recapiti per ricevere copia del contratto firmato (precompilati,
                modificabili dal firmatario). */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">
                Dove vuoi ricevere il contratto firmato?
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="tuaemail@esempio.com"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    WhatsApp
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={notifyPhonePrefix}
                      onChange={(e) => setNotifyPhonePrefix(e.target.value)}
                      className="px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="+39">🇮🇹 +39</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+33">🇫🇷 +33</option>
                      <option value="+34">🇪🇸 +34</option>
                      <option value="+41">🇨🇭 +41</option>
                      <option value="+43">🇦🇹 +43</option>
                      <option value="+32">🇧🇪 +32</option>
                      <option value="+31">🇳🇱 +31</option>
                      <option value="+351">🇵🇹 +351</option>
                      <option value="+30">🇬🇷 +30</option>
                      <option value="+45">🇩🇰 +45</option>
                      <option value="+46">🇸🇪 +46</option>
                      <option value="+47">🇳🇴 +47</option>
                      <option value="+358">🇫🇮 +358</option>
                      <option value="+48">🇵🇱 +48</option>
                      <option value="+36">🇭🇺 +36</option>
                      <option value="+420">🇨🇿 +420</option>
                      <option value="+7">🇷🇺 +7</option>
                      <option value="+86">🇨🇳 +86</option>
                      <option value="+81">🇯🇵 +81</option>
                      <option value="+82">🇰🇷 +82</option>
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+61">🇦🇺 +61</option>
                      <option value="+64">🇳🇿 +64</option>
                      <option value="+55">🇧🇷 +55</option>
                      <option value="+52">🇲🇽 +52</option>
                      <option value="+54">🇦🇷 +54</option>
                      <option value="+27">🇿🇦 +27</option>
                    </select>
                    <input
                      type="tel"
                      value={notifyPhoneNumber}
                      onChange={(e) => setNotifyPhoneNumber(e.target.value)}
                      placeholder="333 1234567"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-blue-700">
                  Riceverai automaticamente il contratto firmato via email e/o WhatsApp ai recapiti indicati.
                </p>
              </div>
            </div>

            <div className="border-2 border-gray-300 rounded-md p-2 bg-white">
              <canvas
                ref={canvasRef}
                className="w-full h-40 touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={endDrawing}
                onMouseLeave={endDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={endDrawing}
              ></canvas>
            </div>
            
            <div className="mt-4 flex justify-between">
              <button
                type="button"
                onClick={clearSignature}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear Signature
              </button>
              
              <button
                type="button"
                onClick={submitSignature}
                disabled={!signatureData || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Complete Signature
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Signatories Status */}
        {allSignatories.length > 1 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Signature Status</h3>
            <div className="space-y-3">
              {allSignatories.map((signatory) => (
                <div key={signatory.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="font-medium text-gray-900">{signatory.name}</p>
                      <p className="text-sm text-gray-500">{signatory.role}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    signatory.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : signatory.status === 'declined'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {signatory.status === 'completed' ? 'Signed' : 
                     signatory.status === 'declined' ? 'Declined' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}